import { GoogleLogo, User, SignOut, Shield } from "@phosphor-icons/react";
import { useAuth } from "../lib/AuthProvider";
import { useState, useRef, useEffect } from "react";

/**
 * Header sign-in / user-menu component. Two states:
 *   • Anonymous → shows a "Sign in with Google" button.
 *   • Signed in → avatar + name; click to open a dropdown with email, admin
 *     badge, and Sign Out.
 */
export const AuthButton = () => {
  const { user, isAdmin, ready, signIn, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!ready) return <div className="w-24 h-8 bg-zinc-100 rounded-full animate-pulse" />;

  if (!user) {
    return (
      <button
        type="button" onClick={signIn}
        data-testid="signin-btn"
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white border border-zinc-200 hover:border-[#6043EF] text-[11px] font-bold uppercase tracking-wider text-[#09090B] hover:text-[#6043EF] transition-colors"
      >
        <GoogleLogo size={13} weight="fill" className="text-[#EA4335]" />
        Sign In
      </button>
    );
  }

  const avatar = user.photoURL;
  const initial = (user.displayName || user.email || "U").charAt(0).toUpperCase();

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button" onClick={() => setOpen((v) => !v)}
        data-testid="user-menu-btn"
        className="inline-flex items-center gap-1.5 h-8 pl-1 pr-2 rounded-full bg-white border border-zinc-200 hover:border-[#6043EF] transition-colors"
        aria-expanded={open}
      >
        {avatar ? (
          <img src={avatar} alt="" className="w-6 h-6 rounded-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <span className="w-6 h-6 rounded-full bg-gradient-to-br from-[#6043EF] to-violet-500 text-white text-[10px] font-bold flex items-center justify-center">{initial}</span>
        )}
        <span className="text-[11px] font-bold text-[#09090B] max-w-[100px] truncate">
          {user.displayName?.split(" ")[0] || "User"}
        </span>
        {isAdmin && <Shield size={11} weight="fill" className="text-emerald-600" />}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-64 bg-white border border-zinc-200 rounded-xl shadow-[0_8px_28px_-8px_rgba(15,15,20,0.25)] z-50 overflow-hidden"
          data-testid="user-menu-dropdown"
        >
          <div className="p-3 border-b border-zinc-100">
            <div className="text-[13px] font-bold text-[#09090B] truncate">
              {user.displayName || "—"}
            </div>
            <div className="text-[11px] text-zinc-500 truncate flex items-center gap-1">
              <User size={11} weight="fill" />
              {user.email}
            </div>
            {isAdmin && (
              <span className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-wider">
                <Shield size={10} weight="fill" /> Admin
              </span>
            )}
          </div>
          {isAdmin && (
            <a
              href="/admin"
              className="flex items-center gap-2 px-3 py-2 text-[12px] font-semibold text-[#09090B] hover:bg-zinc-50 transition-colors"
              data-testid="user-menu-admin"
            >
              <Shield size={12} weight="fill" className="text-emerald-600" /> Admin Console
            </a>
          )}
          <button
            type="button" onClick={() => { setOpen(false); signOut(); }}
            data-testid="user-menu-signout"
            className="flex w-full items-center gap-2 px-3 py-2 text-[12px] font-semibold text-rose-600 hover:bg-rose-50 transition-colors"
          >
            <SignOut size={12} weight="bold" /> Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

export default AuthButton;
